import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Wallet, Heart, MessageCircle } from 'lucide-react';

export const PROVIDERS = [
  { id: 'wave', name: 'Wave', color: '#1B6BF7' },
  { id: 'orange', name: 'Orange Money', color: '#FF6B00' },
  { id: 'mtn', name: 'MTN MoMo', color: '#F8C108' },
  { id: 'moov', name: 'Moov Money', color: '#3DBA75' },
];

export const AMOUNT_PRESETS = [1000, 2500, 5000, 10000, 25000, 50000];

export const formatN = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);

export function AmountPicker({ amount, setAmount, accent }) {
  const amt = Number(amount) || 0;
  const fee = amt < 10000 ? 0 : amt <= 100000 ? Math.round(amt * 0.08) : Math.round(amt * 0.03);
  const net = amt - fee;
  const num = Number(amount);
  return (
    <div>
      <Label className="text-[13px]">Montant du don</Label>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {AMOUNT_PRESETS.map((v) => (
          <button
            key={v}
            onClick={() => setAmount(v)}
            className={`px-3 py-1.5 rounded-full text-[12.5px] border btn-anim ${num === v ? 'text-white' : 'text-neutral-700 bg-white'}`}
            style={num === v ? { background: accent, borderColor: accent } : { borderColor: 'rgba(0,0,0,0.08)' }}
          >
            {formatN(v)} FCFA
          </button>
        ))}
      </div>
      <div className="mt-3 relative">
        <Input type="number" min={100} step={100} value={amount} onChange={(e) => setAmount(e.target.value)} className="h-12 pr-14 text-[18px] font-semibold" />
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[12.5px] text-neutral-500 font-medium">FCFA</span>
      </div>
      {amt > 0 && (
        <p className="mt-2 text-[11.5px] text-neutral-500" data-testid="fee-transparency-note">
          {fee === 0 ? (
            <span className="text-[#128C4A] font-medium">0 frais — 100 % pour la cagnotte 💚</span>
          ) : (
            <>Frais plateforme : {formatN(fee)} FCFA ({amt <= 100000 ? '8' : '3'} %) · la cagnotte recevra <span className="font-semibold text-neutral-800">{formatN(net)} FCFA</span></>
          )}
        </p>
      )}
    </div>
  );
}

export function ProviderPicker({ provider, setProvider }) {
  return (
    <div>
      <Label className="text-[13px]">Payer avec</Label>
      <RadioGroup value={provider} onValueChange={setProvider} className="mt-2 grid grid-cols-2 gap-2">
        {PROVIDERS.map((p) => (
          <label
            key={p.id}
            htmlFor={`don-${p.id}`}
            className={`cursor-pointer rounded-2xl border p-3 flex items-center gap-2.5 btn-anim ${provider === p.id ? 'bg-white' : 'bg-white/60 border-black/5 hover:bg-white'}`}
            style={provider === p.id ? { borderColor: p.color } : {}}
          >
            <RadioGroupItem id={`don-${p.id}`} value={p.id} />
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${p.color}18`, color: p.color }}>
              <Wallet className="w-3.5 h-3.5" />
            </div>
            <div className="text-[13px] font-medium text-neutral-800">{p.name}</div>
          </label>
        ))}
      </RadioGroup>
    </div>
  );
}

export function DonorFields({ donorName, setDonorName, donorPhone, setDonorPhone }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <Label className="text-[13px]">Votre prénom (facultatif)</Label>
        <Input data-testid="donor-name-input" value={donorName} onChange={(e) => setDonorName(e.target.value)} placeholder="Awa, Kouadio..." className="mt-1.5 h-10" />
      </div>
      <div>
        <Label className="text-[13px]">Votre numéro (facultatif)</Label>
        <Input data-testid="donor-phone-input" value={donorPhone} onChange={(e) => setDonorPhone(e.target.value)} placeholder="+225 07..." className="mt-1.5 h-10" inputMode="tel" />
      </div>
    </div>
  );
}

export function MessageField({ isYako, message, setMessage, anonymous, setAnonymous, hideAnonymous }) {
  return (
    <div>
      <Label className="text-[13px]">Message (facultatif)</Label>
      <Textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={3}
        placeholder={isYako ? 'Un mot de soutien...' : 'Un mot de f\u00e9licitations...'}
        className="mt-1.5 resize-none"
        maxLength={280}
      />
      <div className="text-[11px] text-neutral-500 mt-1 flex justify-between">
        {hideAnonymous ? <span /> : (
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="accent-neutral-800" />
            <span>Donner anonymement</span>
          </label>
        )}
        <span>{message.length}/280</span>
      </div>
    </div>
  );
}

export function DonationSuccess({ amount, providerName, accent, cagnotte }) {
  const shareWhatsApp = () => {
    const url = `${window.location.origin}/cagnotte/${cagnotte?.id}`;
    const text = `Je viens de soutenir « ${cagnotte?.title} » sur Fodindin ! Rejoignez-moi : ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  };
  return (
    <div className="px-6 py-10 text-center">
      <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center" style={{ background: `${accent}18`, color: accent }}>
        <Heart className="w-7 h-7" fill="currentColor" />
      </div>
      <h3 className="mt-4 text-[22px] font-semibold text-[#1a1d1a]">Merci pour votre solidarité !</h3>
      <p className="mt-2 text-[14px] text-neutral-600">
        Votre don de <span className="font-semibold text-[#1a1d1a]">{formatN(amount)} FCFA</span> a bien été enregistré. Le bénéficiaire recevra les fonds sur son compte {providerName}.
      </p>
      {cagnotte?.id && (
        <button
          type="button"
          onClick={shareWhatsApp}
          data-testid="donation-success-whatsapp-share"
          className="btn-anim mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#25D366] hover:bg-[#1fbb59] text-white text-[13.5px] font-medium transition-colors"
        >
          <MessageCircle className="w-4 h-4" /> Partager sur WhatsApp
        </button>
      )}
    </div>
  );
}
